import React, { useEffect, useState } from "react";
import {
  Button,
  Spinner,
} from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { Dismiss20Regular, Save20Regular } from "@fluentui/react-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getAccessToken } from "../../Services/GraphServices";
import {
  createVariable,
  updateVariable,
  getVariable,
} from "../../Services/HeedifyServices";
import "../CSS/VariablesComponent.css";
import VariableForm from "./VariableForm";
import { useAbility } from "../../Contexts/AbilityContext";
import { ReadOnlyFieldset } from "../GenericComponent/ReadOnlyFieldset";
import { useNotification } from '../GenericComponent/NotificationSystem';

export default function VariablesComponent(props) {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { variableId } = useParams();
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const isEditMode = variableId && variableId !== "add";
  const isCreateMode = !isEditMode;

  const [variable, setVariable] = useState({
    scope: "",
    displayName: "",
    DB: null,
  });

  const [loading, setLoading] = useState(false);
  const [formValid, setFormValid] = useState(false);

  // Permission check
  const canCreate = props.SCOPES_NAMES?.some((scope) =>
    ability?.can("create", `Flow.main@${scope}`)
  );
  const canUpdate =
    isEditMode && variable.scope && ability?.can("update", `Flow.main@${variable.scope}`);
  // L'écran s'ouvre sur `canRead` : il doit savoir se rendre sans droit d'écriture.
  const readOnly = isEditMode && !canUpdate;

  useEffect(() => {
    if (isEditMode) {
      // Mode Edit: charger depuis location.state ou API
      if (location.state?.variable) {
        setVariable(location.state.variable);
      } else {
        fetchVariable();
      }
    }
  }, [variableId]);

  const fetchVariable = async () => {
    if (!variableId || !props.ACTIVE_TENANT) return;

    setLoading(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      // On ne connaît pas le scope, donc chercher dans tous les scopes
      for (const scope of props.SCOPES_NAMES || []) {
        const result = await getVariable(props.ACTIVE_TENANT, scope, variableId, token);
        if (result) {
          setVariable(result);
          break;
        }
      }
    } catch (error) {
      console.error("Error fetching variable:", error);
      showError(t("ERROR_FETCHING_VARIABLE"), error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
   
    try {
      const token = await getAccessToken(instance, activeAccount);

      if (isCreateMode) {
        const result = await createVariable(
          props.ACTIVE_TENANT,
          variable.scope,
          variable,
          token
        );

        if (result.success && result.message === "created") {
          // Mettre à jour Redux avec la nouvelle variable
          if (result.data && props.setVariables && props.VARIABLES) {
            props.setVariables([...props.VARIABLES, result.data]);
          }
          showSuccess(t("VARIABLE_CREATED_SUCCESS"));
          navigate("/variables");
        } else {
          const errorMsg = result.message || "Erreur inconnue";
          showError(t("VARIABLE_CREATION_FAILED"), errorMsg);
        }
      } else {
        const result = await updateVariable(
          props.ACTIVE_TENANT,
          variable.scope,
          variableId,
          variable,
          token
        );

        if (result.success && result.message === "updated") {
          // Mettre à jour Redux avec la variable modifiée
          if (props.setVariables && props.VARIABLES) {
            // Utiliser result.data si disponible, sinon utiliser les données locales avec l'id
            const updatedVariable = result.data || { ...variable, id: variableId };
            props.setVariables(
              props.VARIABLES.map((v) => (v.id === variableId ? updatedVariable : v))
            );
          }
          showSuccess(t("VARIABLE_UPDATED_SUCCESS"));
          navigate("/variables");
        } else {
          const errorMsg = result.message || "Erreur inconnue";
          showError(t("VARIABLE_UPDATE_FAILED"), errorMsg);
        }
      }
    } catch (error) {
      console.error("Error saving variable:", error);
      showError(isEditMode ? t("VARIABLE_UPDATE_ERROR") : t("VARIABLE_CREATION_ERROR"), error?.message);
    }
  };

  const handleCancel = () => {
    navigate("/variables");
  };

 

  return (
    <div className="variables-component-container">
      
        <div className="action-buttons-variables">
          <Button
            appearance="secondary"
            icon={<Dismiss20Regular />}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>
          {/* Masqué, pas grisé : grisé, il laisse croire qu'il manque une saisie. */}
          {!readOnly && (
            <Button
              appearance="primary"
              icon={<Save20Regular />}
              onClick={handleSubmit}
              disabled={!formValid || loading}
            >
              {t("save")}
            </Button>
          )}
        </div>
     

      <ReadOnlyFieldset readOnly={readOnly}>
        <VariableForm
          variable={variable}
          setVariable={setVariable}
          isUpdate={isEditMode}
          setFormValid={setFormValid}
          SCOPES_NAMES={props.SCOPES_NAMES}
        />
      </ReadOnlyFieldset>
    </div>
  );
}
